import React from 'react';
import { Button } from './ui';

interface ErrorBoundaryProps {
  children: React.ReactNode;
  /** Optional fallback UI to render instead of the default error message. */
  fallback?: React.ReactNode;
  /** If true, renders a compact inline error suitable for embedding within a page section. */
  inline?: boolean;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * React error boundary that catches render errors in its children and displays
 * a fallback UI instead of crashing the entire app to a white screen.
 *
 * Usage:
 *   <ErrorBoundary>
 *     <SomeComponent />
 *   </ErrorBoundary>
 *
 *   <ErrorBoundary inline>
 *     <PreviewFrame ... />
 *   </ErrorBoundary>
 */
class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('[ErrorBoundary] Caught render error:', error);
    console.error('[ErrorBoundary] Component stack:', errorInfo.componentStack);
  }

  handleReset = (): void => {
    this.setState({ hasError: false, error: null });
  };

  render(): React.ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }

    // Allow a custom fallback to override the default UI
    if (this.props.fallback) {
      return this.props.fallback;
    }

    // Compact inline variant for wrapping sections within a page
    if (this.props.inline) {
      return (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 'var(--space-8)',
            gap: 'var(--space-4)',
            height: '100%',
            minHeight: '200px',
            backgroundColor: '#fef2f2',
            border: 'var(--border-thick)',
            borderColor: 'var(--color-error)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-display)',
              fontWeight: 700,
              fontSize: 'var(--text-lg)',
            }}
          >
            Failed to render this section
          </div>
          <p
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--text-sm)',
              color: 'var(--color-muted)',
              textAlign: 'center',
              margin: 0,
            }}
          >
            {this.state.error?.message || 'An unexpected error occurred'}
          </p>
          <Button variant="secondary" size="sm" onClick={this.handleReset}>
            Try Again
          </Button>
        </div>
      );
    }

    // Full-page variant for the top-level boundary
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 'var(--space-8)',
          gap: 'var(--space-6)',
          backgroundColor: 'var(--color-bg)',
        }}
      >
        <div
          style={{
            width: '80px',
            height: '80px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'var(--color-error)',
            color: 'var(--color-bg)',
          }}
        >
          <svg
            width="40"
            height="40"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="3"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </div>

        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 'var(--text-3xl)',
            textTransform: 'uppercase',
            margin: 0,
          }}
        >
          Something went wrong
        </h1>

        <p
          style={{
            fontFamily: 'var(--font-body)',
            fontSize: 'var(--text-lg)',
            color: 'var(--color-muted)',
            textAlign: 'center',
            maxWidth: '500px',
            margin: 0,
          }}
        >
          An unexpected error prevented the page from rendering. You can try
          again or return to the home page.
        </p>

        <div
          style={{
            padding: 'var(--space-4)',
            backgroundColor: 'var(--danger-bg)',
            border: 'var(--border-thick)',
            borderColor: 'var(--color-error)',
            fontFamily: 'var(--font-mono)',
            fontSize: 'var(--text-sm)',
            maxWidth: '600px',
            width: '100%',
            wordBreak: 'break-word',
          }}
        >
          <strong>Error:</strong> {this.state.error?.message || 'Unknown error'}
        </div>

        <div style={{ display: 'flex', gap: 'var(--space-4)' }}>
          <Button variant="ghost" onClick={() => { window.location.href = '/'; }}>
            Go Home
          </Button>
          <Button variant="primary" onClick={this.handleReset}>
            Try Again
          </Button>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
